// Phase 9.3.B — Portal brand assets API client (2026-05-12).
//
// Typed wrappers around the Phase 9.3.A backend:
//   GET    /portal/brand-assets
//   GET    /portal/brand-assets/:id
//   POST   /portal/brand-assets
//   DELETE /portal/brand-assets/:id
//
// Plus the unified file-download endpoint used by BOTH this module AND
// the order-detail proof gallery (Phase 9.2 rewire):
//   GET    /portal/files/:fileId/download
//
// Upload is a two-step flow: (1) POST /portal/brand-assets gets a
// presigned upload URL, (2) browser PUTs bytes to R2 directly. Helper
// `uploadBrandAssetFile` chains both. The browser PUT call does NOT go
// through the api() wrapper — it's a direct fetch to Cloudflare R2.
import { api } from "@/lib/api";

export type BrandAssetTag =
  | "logo"
  | "pattern"
  | "typography"
  | "brand_guide"
  | "mood_board"
  | "other";

export interface PortalBrandAsset {
  id: string;
  tag: BrandAssetTag;
  fileId: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface PortalBrandAssetListResult {
  rows: PortalBrandAsset[];
  nextCursor: { uploadedAt: string; id: string } | null;
}

export interface CreateBrandAssetResponse {
  brand_asset_id: string;
  file_id: string;
  upload_url: string;
  live: boolean;
}

export interface FileDownloadResponse {
  url: string;
  expires_at: string;
  source: "brand_asset" | "proof";
  mime_type: string;
  original_filename: string;
}

// ─── Fetchers ────────────────────────────────────────────────────────

export interface ListBrandAssetsFilters {
  tag?: BrandAssetTag[];
  limit?: number;
  cursor?: { uploadedAt: string; id: string } | null;
}

export async function fetchPortalBrandAssets(
  filters: ListBrandAssetsFilters = {},
): Promise<PortalBrandAssetListResult> {
  const params = new URLSearchParams();
  for (const t of filters.tag ?? []) params.append("tag", t);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.cursor) {
    params.set("cursor_uploaded_at", filters.cursor.uploadedAt);
    params.set("cursor_id", filters.cursor.id);
  }
  const qs = params.toString();
  return api<PortalBrandAssetListResult>(
    `/portal/brand-assets${qs ? `?${qs}` : ""}`,
  );
}

export async function deletePortalBrandAsset(id: string): Promise<void> {
  await api<void>(`/portal/brand-assets/${id}`, { method: "DELETE" });
}

export interface UploadBrandAssetArgs {
  file: File;
  tag: BrandAssetTag;
}

/**
 * End-to-end upload: presign + PUT bytes to R2 + return the new asset
 * id. Throws on any network or HTTP error so the caller can show a
 * single error message.
 *
 * The browser PUT to R2 doesn't go through the `api()` wrapper — it's a
 * cross-origin direct upload and never includes our auth header.
 */
export async function uploadPortalBrandAsset(
  args: UploadBrandAssetArgs,
): Promise<{ brandAssetId: string; fileId: string; live: boolean }> {
  const presign = await api<CreateBrandAssetResponse>("/portal/brand-assets", {
    method: "POST",
    body: JSON.stringify({
      filename: args.file.name,
      mime_type: args.file.type || "application/octet-stream",
      size_bytes: args.file.size,
      tag: args.tag,
    }),
  });

  // Step 2: PUT the bytes directly to Cloudflare R2. The presigned URL
  // is short-lived (10 min) and tied to the file's content-type.
  if (presign.live) {
    const putRes = await fetch(presign.upload_url, {
      method: "PUT",
      headers: { "Content-Type": args.file.type || "application/octet-stream" },
      body: args.file,
    });
    if (!putRes.ok) {
      throw new Error(`R2 upload failed: ${putRes.status} ${putRes.statusText}`);
    }
  }
  // When `live: false` (dev env without R2 creds), the backend already
  // registered the rows; the upload URL is a stub. The asset will appear
  // in the gallery as 0-bytes-on-R2 which is fine for dev.

  return {
    brandAssetId: presign.brand_asset_id,
    fileId: presign.file_id,
    live: presign.live,
  };
}

/**
 * Unified file-download endpoint. Works for both brand assets and
 * client-visible proofs — the backend resolver checks both tenancy
 * paths.
 */
export async function fetchPortalFileDownloadUrl(
  fileId: string,
): Promise<FileDownloadResponse> {
  return api<FileDownloadResponse>(`/portal/files/${fileId}/download`);
}

// ─── Display helpers ─────────────────────────────────────────────────

export const TAG_LABEL: Record<BrandAssetTag, string> = {
  logo: "Logo",
  pattern: "Pattern",
  typography: "Typography",
  brand_guide: "Brand guide",
  mood_board: "Mood board",
  other: "Other",
};

/** Brand-locked pill color per tag. Calm cream + peach tones; no rust
 *  here (brand assets are reference material, not urgent). */
export const TAG_PILL_CLASS: Record<BrandAssetTag, string> = {
  logo: "bg-peach-100 text-brown-500",
  pattern: "bg-peach-200 text-brown-500",
  typography: "bg-cream-200 text-brown-500",
  brand_guide: "bg-cream-100 text-brown-500",
  mood_board: "bg-peach-50 text-brown-500",
  other: "bg-cream-50 text-brown-400",
};

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  // A bare "YYYY-MM-DD" (in-hands, invoice date) is a calendar day, not an
  // instant: new Date() reads it as UTC midnight, which shows the day BEFORE
  // anywhere west of London. Read it as local noon instead.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Format bytes → KB/MB/GB. Round to 1 decimal. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function isImageMime(mimeType: string): boolean {
  return mimeType.startsWith("image/");
}
